import { isArrayLength } from './genericHelpers';
import { userDisplayNameAsString } from './data';

/**
 * Retrieves shipping details from a user's profile.
 * @param {Object|null} currentUser - The current user object
 * @returns {Object|undefined} The user's shipping details or undefined if not available
 */
export const getShippingDetails = currentUser => {
  // Early return if currentUser is null/undefined or lacks essential properties
  if (!currentUser || !currentUser.id || !currentUser.attributes?.profile) {
    return undefined;
  }

  return currentUser.attributes.profile.protectedData?.shippingDetails;
};

export const getUserEmail = currentUser => currentUser?.attributes?.email || null;
export const getDisplayName = currentUser => userDisplayNameAsString(currentUser, '');

export function getUserCartDetails(user) {
  // Consolidated null/undefined checks for robustness:
  if (!user || !user.id) {
    // Handle missing user or user ID gracefully - return empty array for consistency
    return [];
  }

  // Combine optional chaining with default value for concise and safe access:
  const protectedData = user.attributes?.profile?.protectedData || {};
  return protectedData.cartItems ?? [];
}

// How many of each cart listing the user wants: { listingId: quantity }.
export const getUserCartQuantities = user =>
  user?.attributes?.profile?.protectedData?.cartQuantities || {};

export const getUserShippingAddress = user => {
  if (!user?.id) return null;
  const { shippingAddress = {} } = user?.attributes?.profile?.privateData || {};
  return shippingAddress;
};

export const getShippingAddressID = user => {
  return user?.attributes?.profile?.publicData?.shippingAddressId;
};

/**
 * Returns the title of the listing associated with a transaction.
 * @param {object} tx - The transaction object.
 * @returns {string|null} The listing title, or null if input is invalid or listing title is missing.
 */
export const getTxTitle = tx => {
  const currentListing = tx?.listing;
  const title = currentListing && currentListing.attributes && currentListing.attributes.title;
  return title;
};

export const getStoreNameOrDisplayName = author => {
  const displayName = getDisplayName(author);
  return displayName;
};

/**
 * Checks if a transaction is a shipping transaction.
 *
 * @param {Object} tx - The transaction object.
 * @returns {number} - The offer price of the transaction
 */
export const getOfferPrice = tx => {
  return tx && tx.id && tx?.attributes?.protectedData?.offerParams?.offerPrice;
};

export const getOfferParams = tx => {
  return tx && tx.id && tx?.attributes?.protectedData?.offerParams;
};

export const isOwnOffer = (customer, currentUser) => {
  return customer?.id?.uuid === currentUser?.id?.uuid;
};

/**
 * Returns the URL of the image associated with a transaction's listing.
 * @param {object} tx - The transaction object.
 * @returns {string} The URL of the image, or a default image URL if input is invalid or image is missing.
 */
export const getTxImage = tx => {
  const currentListing = tx?.listing;
  const firstImage =
    currentListing && currentListing.images && currentListing.images.length > 0
      ? currentListing.images[0]
      : null;

  const sharetribeImage =
    firstImage &&
    firstImage.attributes &&
    firstImage.attributes.variants &&
    firstImage.attributes.variants['square-small2x'] &&
    firstImage.attributes.variants['square-small2x'].url;

  return sharetribeImage;
};

export const getSimilarParams = (listing, currentUser) => {
  const publicData = listing?.attributes?.publicData;
  return {
    brand: publicData?.brand,
    size: publicData?.size,
  };
};

export const doesAcceptOffer = listing => {
  return listing?.attributes?.publicData?.acceptOffers;
};

export const getListingShippingParams = (listing, isDomestic) => {
  const publicData = listing?.attributes?.publicData;
  return isDomestic ? publicData?.domesticParams : publicData?.internationalParams;
};

export const isDomesticShipping = (author, currentUser, customerCountryMaybe) => {
  const authorCountry = author?.attributes?.profile?.publicData?.country;
  const currentUserCountry =
    customerCountryMaybe || currentUser?.attributes?.profile?.privateData?.shippingAddress?.country;

  return authorCountry === currentUserCountry;
};

/**
 * Constructs a user's full name from their profile attributes.
 * @param {Object|null} user - The user object containing profile attributes
 * @returns {string|null} The user's full name (firstName + lastName) or displayName, or null if invalid
 */
export const getUserFullName = user => {
  // Validate input
  if (!user || !user.id || !user.attributes?.profile) {
    return null;
  }

  const { firstName, lastName, displayName } = user.attributes.profile;

  // Combine firstName and lastName, ensuring no extra spaces
  const fullName = [firstName, lastName]
    .filter(name => typeof name === 'string' && name.trim().length > 0)
    .join(' ')
    .trim();

  // Return fullName if non-empty, otherwise fallback to displayName
  return fullName || displayName || null;
};

/**
 * Retrieves shipping carriers from a listing object.
 * @param {Object} listing - The listing object containing shipping carrier data.
 * @returns {Object|null} The shipping carriers object or null if not found or invalid.
 */
export const getListingShippingCarriers = listing => {
  if (!listing?.id?.uuid) {
    return null;
  }
  return listing.attributes?.publicData?.shippingCarriers ?? {};
};

/**
 * Filters shipping rates to match carriers from listingShippingCarriers.
 * The seller's configuration is the source of truth — only their chosen
 * carrier/service pairs are shown. If the seller has no carrier config at all,
 * falls back to a curated default set so existing listings keep working.
 * @param {Object} listingShippingCarriers - The shipping carriers object from the listing.
 * @param {Array<Object>} rates - The rates array from EasyPost API.
 * @returns {Array<Object>} Filtered rates matching the seller's config, or defaults if no config.
 */
export const filterMatchingCarrierRates = (listingShippingCarriers, rates) => {
  // No rates at all — nothing to filter
  if (!rates?.length) return [];

  // Seller has no carrier config → fall back to curated defaults so existing listings work
  if (!listingShippingCarriers) {
    return selectDefaultRates(rates);
  }
  // Extract valid carrier-service pairs from listingShippingCarriers
  const validCarriers = new Set();
  // Support both new format (domesticCarriers/internationalCarriers) and old 4-array format
  const carrierLists = [
    listingShippingCarriers.domesticCarriers,
    listingShippingCarriers.internationalCarriers,
    listingShippingCarriers.defaultDomesticCarrier,
    listingShippingCarriers.defaultInternationalCarrier,
    listingShippingCarriers.domesticAdditional,
    listingShippingCarriers.internationalAdditional,
  ].filter(Boolean);

  carrierLists.forEach(list =>
    list?.forEach(({ carrier, name }) => {
      if (carrier && name) {
        // Normalize both carrier and service name to lowercase for consistent matching
        validCarriers.add(`${carrier.toLowerCase()}:${name.toLowerCase()}`);
      }
    })
  );

  // Config object exists but holds no usable carrier/service pairs (e.g.
  // flat-rate listings created after carrier selection stopped being part
  // of that flow) — same fallback as a missing config, so the seller can
  // still buy a label from the curated default set.
  if (validCarriers.size === 0) {
    return selectDefaultRates(rates);
  }

  // Normalize carrier names for comparison (handle variants like "FedExDefault", "UPSDAP")
  const normalizeCarrier = carrier => {
    return carrier
      .toLowerCase()
      .replace('fedexdefault', 'fedex')
      .replace('upsmailinnovations', 'ups')
      .replace('upsdap', 'ups'); // UPS DAP (Delivered At Place) is a UPS service
  };

  // Normalize service names (remove underscores, handle different formats)
  const normalizeService = service => {
    return service
      .toLowerCase()
      .replace(/_/g, '')  // "ground_home_delivery" -> "groundhomedelivery"
      .replace(/-/g, ''); // "3-day-select" -> "3dayselect"
  };

  // Filter rates that match valid carrier-service pairs
  const matchedRates = rates.filter(rate => {
    const normalizedCarrier = normalizeCarrier(rate.carrier);
    const normalizedService = normalizeService(rate.service);
    const carrierService = `${normalizedCarrier}:${normalizedService}`;

    // Check for exact match first
    if (validCarriers.has(carrierService)) {
      return true;
    }

    // Also check without underscores in the stored names (for compatibility)
    const hasMatch = Array.from(validCarriers).some(validPair => {
      const normalizedValidPair = validPair.replace(/_/g, '').replace(/-/g, '');
      return normalizedValidPair === carrierService;
    });

    return hasMatch;
  });
  // Return only seller-configured rates — no fallback to all carriers
  return matchedRates;
};

/**
 * Selects up to five rates sorted by cost from lowest to highest.
 * @param {Array<Object>} rates - The rates array from EasyPost API.
 * @returns {Array<Object>} A curated list of up to five rates sorted by cost.
 */
const selectDefaultRates = rates => {
  if (!isArrayLength(rates)) {
    return [];
  }

  // Sort all rates by cost (ascending)
  const sortedRates = [...rates].sort((a, b) => parseFloat(a.rate) - parseFloat(b.rate));

  // Return up to 5 lowest-cost rates
  return sortedRates.slice(0, 5);
};
export const getDeliveryMethod = listing => {
  if (!listing?.id?.uuid) {
    return null;
  }
  return listing.attributes?.publicData?.deliveryMethod ?? {};
};

/**
 * Checks if the listingShippingCarriers object has any non-empty carrier arrays.
 * @param {Object} listingShippingCarriers - The object containing carrier arrays.
 * @returns {boolean} True if any carrier array has values, false otherwise.
 */
export const hasCarriers = listingShippingCarriers => {
  if (!listingShippingCarriers || typeof listingShippingCarriers !== 'object') {
    return false;
  }

  const carrierKeys = [
    'domesticCarriers',
    'internationalCarriers',
    // old format fallback
    'defaultDomesticCarrier',
    'defaultInternationalCarrier',
    'domesticAdditional',
    'internationalAdditional',
  ];

  return carrierKeys.some(
    key => Array.isArray(listingShippingCarriers[key]) && listingShippingCarriers[key].length > 0
  );
};

// Backward-compatible cartItems parse:
// Old transactions store cartItems as a JSON string; new transactions store a real array.
export const parseCartItems = raw => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

export const extractTransactionCartItems = tx => {
  if (!tx || !tx.id || !tx.id.uuid) {
    return null;
  }

  return parseCartItems(tx.attributes?.protectedData?.cartItems);
};
export const extractTransactionTrackingInfo = tx => {
  if (!tx || !tx.id || !tx.id.uuid) {
    return null;
  }

  const trackingInfo = tx.attributes?.metadata?.trackingInfo
    ? tx.attributes.metadata.trackingInfo
    : {};

  return trackingInfo;
};

// Extract comprehensive shipping and delivery information from transaction
export const extractTransactionDeliveryInfo = tx => {
  if (!tx || !tx.id || !tx.id.uuid) {
    return null;
  }

  const protectedData = tx.attributes?.protectedData || {};
  const metadata = tx.attributes?.metadata || {};
  const deliveryMethod = protectedData.deliveryMethod;

  // Base delivery information
  const deliveryInfo = {
    method: deliveryMethod,
    deliveryDetails: protectedData.deliveryDetails || null,
    shippingDetails: protectedData.shippingDetails || null,
    // Extract shipping label from metadata (primary) or protectedData (legacy fallback)
    shippingLabel: metadata.shippingLabel || protectedData.shippingLabel || null,
  };

  // For shipping deliveries, extract comprehensive shipping data
  if (deliveryMethod === 'shipping' && protectedData.shippingDetails) {
    // shippingDetails is now saved directly as object (not JSON string)
    const shippingDetails = protectedData.shippingDetails;

    deliveryInfo.shipping = {
      carrier: shippingDetails.carrier,
      service: shippingDetails.service,
      price: shippingDetails.finalPrice || shippingDetails.rate,
      currency: shippingDetails.currency || 'USD',
      deliveryDays: shippingDetails.deliveryDays,
      deliveryDate: shippingDetails.deliveryDate,
      deliveryDateGuaranteed: shippingDetails.deliveryDateGuaranteed,
      isGuaranteed: shippingDetails.estDeliveryDateGuaranteed,
      rateId: shippingDetails.rateId,
      shipmentId: shippingDetails.shipmentId,
      isFlatRate: shippingDetails.isFlatRate,
      isDomestic: shippingDetails.isDomestic,
      isInternational: shippingDetails.isInternational,
      selectedAt: shippingDetails.selectedAt,
    };
  }

  // For pickup deliveries, extract pickup location
  if (deliveryMethod === 'pickup' && protectedData.deliveryDetails?.pickupLocation) {
    deliveryInfo.pickup = {
      location: protectedData.deliveryDetails.pickupLocation,
      selectedAt: protectedData.deliveryDetails.selectedAt,
    };
  }

  return deliveryInfo;
};

export const getDomesticShippingParams = listing => {
  if (!listing?.id?.uuid) return null;
  return listing?.attributes?.publicData?.domesticParams ?? {};
};
export const getInternationalShippingParams = listing => {
  if (!listing?.id?.uuid) return null;
  return listing?.attributes?.publicData?.internationalParams ?? {};
};

// =====================
// Return Policy
// =====================

// Marketplace defaults — used when a listing has no returnPolicy set
// (every pre-existing listing, until the seller edits it). The window
// matches process.edn's auto-complete-no-dispute timer (P14D); the
// previous buyer-facing "7 days" copy was a long-standing typo.
export const DEFAULT_RETURN_POLICY = Object.freeze({
  acceptsReturns: true,
  windowDays: 14,
  returnShippingPaidBy: 'buyer', // 'buyer' | 'seller'
  restockingFeePercent: 0,
});

export const RETURN_WINDOW_OPTIONS = [3, 7, 14, 30];
export const RETURN_WINDOW_SUGGESTED = [14, 30];
export const RETURN_WINDOW_MAX = 90;
export const RESTOCKING_FEE_OPTIONS = [0, 10, 15, 20, 25];

/**
 * Normalize a partially-saved policy object into a complete one, filling
 * missing fields from the marketplace defaults. Custom windows are clamped
 * to 1–RETURN_WINDOW_MAX days.
 */
const normalizeReturnPolicy = saved => ({
  acceptsReturns:
    typeof saved.acceptsReturns === 'boolean'
      ? saved.acceptsReturns
      : DEFAULT_RETURN_POLICY.acceptsReturns,
  windowDays: Number.isFinite(saved.windowDays)
    ? Math.min(Math.max(Math.round(saved.windowDays), 1), RETURN_WINDOW_MAX)
    : DEFAULT_RETURN_POLICY.windowDays,
  returnShippingPaidBy:
    saved.returnShippingPaidBy === 'seller' ? 'seller' : DEFAULT_RETURN_POLICY.returnShippingPaidBy,
  restockingFeePercent: Number.isFinite(saved.restockingFeePercent)
    ? saved.restockingFeePercent
    : DEFAULT_RETURN_POLICY.restockingFeePercent,
});

/**
 * A seller's shop-level return policy from profile publicData, or null when
 * they haven't set one (→ the platform default applies to their listings).
 */
export const getShopReturnPolicy = user => {
  const saved = user?.attributes?.profile?.publicData?.shopReturnPolicy;
  return saved && typeof saved === 'object' ? normalizeReturnPolicy(saved) : null;
};

/**
 * A seller's shop-level default shipping padding from profile publicData:
 * { type: 'fixed', value: <cents> }, or null when unset. New listings
 * pre-fill their padding field from it. Only the 'fixed' type exists today;
 * the type field leaves room for a percentage variant later.
 */
export const getShopShippingPadding = user => {
  const saved = user?.attributes?.profile?.publicData?.shopShippingPadding;
  return saved && typeof saved === 'object' && saved.type === 'fixed' && saved.value > 0
    ? saved
    : null;
};

/**
 * Three-tier return-policy resolution: listing override → seller's shop
 * policy → platform default. Always returns a complete policy plus a
 * `source` field ('listing' | 'shop' | 'platform') saying which tier won.
 *
 * The author is read from the denormalized listing relationship when not
 * passed explicitly; when author data is unavailable the shop tier is
 * skipped (falls through to the platform default, the pre-shop-tier
 * behavior).
 */
export const resolveReturnPolicy = (listing, authorMaybe) => {
  const savedListing = listing?.attributes?.publicData?.returnPolicy;
  if (savedListing && typeof savedListing === 'object') {
    return { ...normalizeReturnPolicy(savedListing), source: 'listing' };
  }
  const author = authorMaybe || listing?.author || null;
  const shopPolicy = getShopReturnPolicy(author);
  if (shopPolicy) {
    return { ...shopPolicy, source: 'shop' };
  }
  return { ...DEFAULT_RETURN_POLICY, source: 'platform' };
};

// =====================
// Saved Addresses Utils
// =====================

const MAX_SAVED_ADDRESSES = 5;

/**
 * Retrieves saved addresses from a user's profile.
 * @param {Object|null} user - The user object
 * @returns {Array} Array of saved addresses or empty array
 */
export const getSavedAddresses = user => {
  if (!user?.id || !user?.attributes?.profile) {
    return [];
  }
  return user.attributes.profile.protectedData?.savedAddresses || [];
};

/**
 * Gets the default address from saved addresses.
 * Returns the one marked as default, or the first one if none is default.
 * @param {Object|null} user - The user object
 * @returns {Object|null} The default address or null
 */
export const getDefaultAddress = user => {
  const addresses = getSavedAddresses(user);
  if (!addresses.length) return null;
  return addresses.find(addr => addr.isDefault) || addresses[0];
};

/**
 * Formats an address for compact display (street, city, state, postal).
 * @param {Object} address - The address object
 * @returns {string} Short formatted address
 */
export const formatAddressShort = address => {
  if (!address) return '';
  const parts = [address.line1, address.city, address.state, address.postal_code].filter(Boolean);
  return parts.join(', ');
};

/**
 * Generates a unique ID for a new address.
 * @returns {string} Unique address ID
 */
export const generateAddressId = () => {
  return `addr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Checks if user can add more addresses (under the limit).
 * @param {Object|null} user - The user object
 * @returns {boolean} True if user can add more addresses
 */
export const canAddMoreAddresses = user => {
  const addresses = getSavedAddresses(user);
  return addresses.length < MAX_SAVED_ADDRESSES;
};

/**
 * Gets the maximum number of allowed addresses.
 * @returns {number} Maximum address count
 */
export const getMaxAddressCount = () => MAX_SAVED_ADDRESSES;

/**
 * Gets the label for an address: the one its owner gave it, else "Address N" (en.json).
 * @param {Object} address - The address object
 * @param {number} index - The index in the addresses array
 * @param {Object} intl - formats the "Address N" fallback
 * @returns {string} Display label for the address
 */
export const getAddressLabel = (address, index, intl) => {
  if (address?.label?.trim()) {
    return address.label.trim();
  }
  return intl.formatMessage({ id: 'SavedAddressCard.defaultLabel' }, { number: index + 1 });
};
